import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { seo } from "@utils/seo";
import i18n from "@modules/shared/localization/i18n";
import {
  createCustomerAddressFn,
  deleteCustomerAddressFn,
  updateCustomerAddressFn,
} from "@modules/account/account.api";
import { customerAddressesQueryOptions } from "@modules/account/account.queryOptions";
import {
  AddressFormModal,
  type AddressFormValues,
} from "@modules/account/components/AddressFormModal";

export const Route = createFileRoute(
  "/$locale/_layout/(protected)/_protected/account/addresses",
)({
  component: AddressesPage,
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(customerAddressesQueryOptions()),
  head: ({ params }) => {
    const t = i18n.getFixedT(params.locale);
    return {
      meta: [
        ...seo({
          title: t("Seo:addresses.title"),
          description: t("Seo:addresses.description"),
        }),
      ],
    };
  },
});

type ModalState =
  | { mode: "closed" }
  | { mode: "add" }
  | {
      mode: "edit";
      addressId: string;
      initialValues: Partial<AddressFormValues>;
      initialIsDefault: boolean;
    };

type ShopifyUserError = {
  code?: string | null;
  field?: readonly (string | null)[] | string[] | null;
  message: string;
};

/**
 * Convert Shopify CA API userErrors into a localized, user-facing message.
 * Uses the `code` enum when present (stable, language-agnostic) and falls back
 * to the raw `message` (always English from Shopify) or a generic fallback.
 */
function translateUserErrors(
  errors: readonly ShopifyUserError[],
  t: (key: string) => string,
  fallbackKey: string,
): string {
  if (errors.length === 0) return t(fallbackKey);
  return errors
    .map((e) => {
      if (e.code) {
        const key = `Account.addresses.errorCodes.${e.code}`;
        const translated = t(key);
        // i18next returns the key itself when no translation is found
        if (translated !== key) return translated;
      }
      return e.message || t(fallbackKey);
    })
    .join("; ");
}

function AddressesPage() {
  const { t } = useTranslation();
  const { locale } = Route.useParams();
  const router = useRouter();
  const queryClient = useQueryClient();

  const addressesOptions = customerAddressesQueryOptions();
  const { data } = useSuspenseQuery(addressesOptions);
  const addresses = data.customer?.addresses?.nodes ?? [];
  const defaultAddressId = data.customer?.defaultAddress?.id ?? null;

  const [modal, setModal] = useState<ModalState>({ mode: "closed" });
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: addressesOptions.queryKey,
    });
    await router.invalidate();
  };

  const createMutation = useMutation({
    mutationFn: async (vars: {
      address: AddressFormValues;
      isDefault: boolean;
    }) =>
      createCustomerAddressFn({
        data: {
          address: toInput(vars.address),
          defaultAddress: vars.isDefault,
        },
      }),
    onSuccess: async (res) => {
      const userErrors = res.customerAddressCreate?.userErrors ?? [];
      if (userErrors.length > 0) {
        toast.error(
          translateUserErrors(userErrors, t, "Account.addresses.saveError"),
        );
        return;
      }
      toast.success(t("Account.addresses.saved"));
      setModal({ mode: "closed" });
      await invalidate();
    },
    onError: (err) => {
      toast.error(
        err instanceof Error ? err.message : t("Account.addresses.saveError"),
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (vars: {
      addressId: string;
      address: AddressFormValues;
      isDefault: boolean;
    }) =>
      updateCustomerAddressFn({
        data: {
          addressId: vars.addressId,
          address: toInput(vars.address),
          defaultAddress: vars.isDefault,
        },
      }),
    onSuccess: async (res) => {
      const userErrors = res.customerAddressUpdate?.userErrors ?? [];
      if (userErrors.length > 0) {
        toast.error(
          translateUserErrors(userErrors, t, "Account.addresses.saveError"),
        );
        return;
      }
      toast.success(t("Account.addresses.saved"));
      setModal({ mode: "closed" });
      await invalidate();
    },
    onError: (err) => {
      toast.error(
        err instanceof Error ? err.message : t("Account.addresses.saveError"),
      );
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (addressId: string) =>
      updateCustomerAddressFn({
        data: { addressId, defaultAddress: true },
      }),
    onSuccess: async (res) => {
      const userErrors = res.customerAddressUpdate?.userErrors ?? [];
      if (userErrors.length > 0) {
        toast.error(
          translateUserErrors(
            userErrors,
            t,
            "Account.addresses.defaultSetError",
          ),
        );
        return;
      }
      toast.success(t("Account.addresses.defaultSet"));
      await invalidate();
    },
    onError: (err) => {
      toast.error(
        err instanceof Error
          ? err.message
          : t("Account.addresses.defaultSetError"),
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (addressId: string) =>
      deleteCustomerAddressFn({ data: { addressId } }),
    onSuccess: async (res) => {
      const userErrors = res.customerAddressDelete?.userErrors ?? [];
      if (userErrors.length > 0) {
        toast.error(
          translateUserErrors(userErrors, t, "Account.addresses.deleteError"),
        );
        return;
      }
      toast.success(t("Account.addresses.deleted"));
      setConfirmDeleteId(null);
      await invalidate();
    },
    onError: (err) => {
      toast.error(
        err instanceof Error ? err.message : t("Account.addresses.deleteError"),
      );
    },
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (values: AddressFormValues, isDefault: boolean) => {
    if (modal.mode === "add") {
      createMutation.mutate({ address: values, isDefault });
    } else if (modal.mode === "edit") {
      updateMutation.mutate({
        addressId: modal.addressId,
        address: values,
        isDefault,
      });
    }
  };

  return (
    <div className="min-h-screen px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <Link
          to="/$locale/account"
          params={{ locale }}
          className="text-text-3 hover:text-primary text-small mb-4 inline-block"
        >
          ← {t("Account.backToAccount")}
        </Link>

        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-2xlarge text-text-1 font-bold">
            {t("Account.addresses.title")}
          </h1>
          <button
            type="button"
            onClick={() => setModal({ mode: "add" })}
            className="bg-primary text-white hover:bg-primary/90 rounded-lg px-4 py-2"
          >
            {t("Account.addresses.addNew")}
          </button>
        </div>

        {addresses.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-text-3 mb-4">{t("Account.addresses.empty")}</p>
            <button
              type="button"
              onClick={() => setModal({ mode: "add" })}
              className="bg-primary text-white hover:bg-primary/90 inline-block rounded-lg px-6 py-3"
            >
              {t("Account.addresses.addFirst")}
            </button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {addresses.map((address) => {
              const isDefault = address.id === defaultAddressId;
              const fullName = [address.firstName, address.lastName]
                .filter(Boolean)
                .join(" ");
              const isConfirmingDelete = confirmDeleteId === address.id;
              const isDeleting =
                deleteMutation.isPending &&
                deleteMutation.variables === address.id;
              const isSettingDefault =
                setDefaultMutation.isPending &&
                setDefaultMutation.variables === address.id;
              return (
                <div
                  key={address.id}
                  className="border-border-5 relative rounded-lg border p-6"
                >
                  {isDefault && (
                    <span className="bg-primary text-small absolute right-4 top-4 rounded-full px-2 py-1 text-white">
                      {t("Account.addresses.default")}
                    </span>
                  )}
                  {fullName && (
                    <p className="text-text-1 mb-2 font-semibold">{fullName}</p>
                  )}
                  {address.company && (
                    <p className="text-text-3">{address.company}</p>
                  )}
                  <p className="text-text-3">
                    {address.address1}
                    {address.address2 && (
                      <>
                        <br />
                        {address.address2}
                      </>
                    )}
                    <br />
                    {[address.city, address.zip].filter(Boolean).join(", ")}
                    {address.territoryCode && (
                      <>
                        <br />
                        {t(`Account.countries.${address.territoryCode}`, {
                          defaultValue: address.territoryCode,
                        })}
                      </>
                    )}
                  </p>
                  {address.phoneNumber && (
                    <p className="text-text-3 mt-2">{address.phoneNumber}</p>
                  )}

                  {isConfirmingDelete ? (
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <span className="text-text-1 text-small">
                        {t("Account.addresses.deleteConfirm")}
                      </span>
                      <button
                        type="button"
                        onClick={() => deleteMutation.mutate(address.id)}
                        disabled={isDeleting}
                        aria-busy={isDeleting}
                        className="bg-error text-white hover:bg-error/90 disabled:opacity-50 disabled:cursor-not-allowed text-small rounded-lg px-3 py-1"
                      >
                        {t("Account.addresses.deleteYes")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        disabled={isDeleting}
                        className="border-border-5 text-text-1 hover:bg-background-2 disabled:opacity-50 disabled:cursor-not-allowed text-small rounded-lg border px-3 py-1"
                      >
                        {t("Account.addresses.cancel")}
                      </button>
                    </div>
                  ) : (
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setModal({
                            mode: "edit",
                            addressId: address.id,
                            initialValues: {
                              firstName: address.firstName ?? "",
                              lastName: address.lastName ?? "",
                              company: address.company ?? "",
                              address1: address.address1 ?? "",
                              address2: address.address2 ?? "",
                              city: address.city ?? "",
                              zip: address.zip ?? "",
                              territoryCode: address.territoryCode ?? "",
                              phoneNumber: address.phoneNumber ?? "",
                            },
                            initialIsDefault: isDefault,
                          })
                        }
                        className="text-primary hover:text-primary/80 text-small"
                      >
                        {t("Account.addresses.edit")}
                      </button>
                      <span className="text-border-5">|</span>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(address.id)}
                        className="text-error hover:text-error/80 text-small"
                      >
                        {t("Account.addresses.delete")}
                      </button>
                      {!isDefault && (
                        <>
                          <span className="text-border-5">|</span>
                          <button
                            type="button"
                            onClick={() =>
                              setDefaultMutation.mutate(address.id)
                            }
                            disabled={isSettingDefault}
                            aria-busy={isSettingDefault}
                            className="text-text-3 hover:text-text-1 text-small disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {t("Account.addresses.setDefault")}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AddressFormModal
        open={modal.mode !== "closed"}
        mode={modal.mode === "edit" ? "edit" : "add"}
        initialValues={modal.mode === "edit" ? modal.initialValues : undefined}
        initialIsDefault={
          modal.mode === "edit" ? modal.initialIsDefault : false
        }
        showDefaultCheckbox
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onClose={() => setModal({ mode: "closed" })}
      />
    </div>
  );
}

// Convert local form values to CA API `CustomerAddressInput`. Empty strings are
// converted to null so Shopify does not persist blank values on optional fields.
function toInput(values: AddressFormValues) {
  const blankToNull = (v: string) => (v.trim() === "" ? null : v.trim());
  return {
    firstName: blankToNull(values.firstName),
    lastName: blankToNull(values.lastName),
    company: blankToNull(values.company),
    address1: blankToNull(values.address1),
    address2: blankToNull(values.address2),
    city: blankToNull(values.city),
    zip: blankToNull(values.zip),
    territoryCode: blankToNull(values.territoryCode),
    phoneNumber: blankToNull(values.phoneNumber),
  };
}
